import { Checkbox, ListItemText, MenuItem } from '@mui/material';
import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledSelect } from '../../shared';

interface Props {
    name: string;
    label: string;
    options: { label: string; value: string }[];
    multiple?: boolean;
    size?: 'small' | 'medium';
}

export const ReportSelectFilter: FC<Props> = ({ name, label, options, multiple = true, size }) => {
    const { t } = useTranslation();
    const form = useFormContext();
    const currentValues = form.watch(name);

    // With only one option, that option is always selected and the filter can't be changed.
    const singleOptionValue = options.length === 1 ? options[0].value : undefined;

    useEffect(() => {
        if (singleOptionValue === undefined) return;
        if (multiple) {
            if (currentValues?.length !== 1 || currentValues[0] !== singleOptionValue) {
                form.setValue(name, [singleOptionValue]);
            }
        } else if (currentValues !== singleOptionValue) {
            form.setValue(name, singleOptionValue);
        }
    }, [singleOptionValue, currentValues, multiple, form, name]);

    const getOptionNameByValue = (value: string): string =>
        options?.find((option) => option.value === value)?.label || t('unknown');

    const formatSelectedValues = (values: string[]) => {
        if (options?.length > 1 && options.length === values?.length) return t('all');
        if (values.length > 2) {
            return values.slice(0, 2).map(getOptionNameByValue).join(', ') + `,... +${values.length - 2}`;
        }
        return values.map(getOptionNameByValue).join(', ');
    };

    return (
        <ControlledSelect
            name={name}
            label={label}
            multiple={multiple}
            items={options.map(({ value }) => value)}
            renderValue={multiple ? (values) => formatSelectedValues(values as string[]) : undefined}
            size={size}
            className="filter"
            disabled={singleOptionValue !== undefined}
        >
            {multiple && (
                <MenuItem value="all">
                    <Checkbox checked={currentValues?.length === (options?.length || 0)} />
                    <ListItemText primary={t('all')} />
                </MenuItem>
            )}
            {options.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                    {multiple && <Checkbox checked={currentValues?.includes(option.value) || false} />}

                    <ListItemText primary={option.label} />
                </MenuItem>
            ))}
        </ControlledSelect>
    );
};
